import React, { Suspense } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { CssVarsProvider } from '@mui/joy/styles';
import CssBaseline from '@mui/joy/CssBaseline';
import GlobalStyles from '@mui/joy/GlobalStyles';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import FormPreviewPage from './pages/FormPreviewPage';
import ProtectedRoute from './components/ProtectedRoute';

const Home = React.lazy(() => import('./pages/Home'));
const Login = React.lazy(() => import('./pages/Login'));
const FormBuilder = React.lazy(() => import('./pages/FormBuilder'));
const ValidationBuilder = React.lazy(() => import('./pages/ValidationBuilder'));
const ClarityConfigurationBuilder = React.lazy(() => import('./pages/ClarityConfigurationBuilder'));
const ComponentStylesList = React.lazy(() => import('./pages/ComponentStylesList'));
const ComponentStyleGroupDetail = React.lazy(() => import('./pages/ComponentStyleGroupDetail'));
const ComponentStyleBuilder = React.lazy(() => import('./pages/ComponentStyleBuilder'));
const Settings = React.lazy(() => import('./pages/Settings'));
const FormStatistics = React.lazy(() => import('./pages/FormStatistics'));

function PageLoader() {
  return null;
}

function AppRoutes() {
  const location = useLocation();
  const isFormPreviewOrPublish =
    location.pathname.includes('/preview') ||
    (location.pathname !== '/' &&
      location.pathname !== '/login' &&
      !location.pathname.startsWith('/forms') &&
      !location.pathname.startsWith('/validations') &&
      !location.pathname.startsWith('/clarity-configurations') &&
      !location.pathname.startsWith('/component-styles') &&
      !location.pathname.startsWith('/settings') &&
      !location.pathname.startsWith('/statistics'));
  return (
    <>
      {!isFormPreviewOrPublish && <ToastContainer autoClose={3000} />}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
          <Route path="/statistics" element={<ProtectedRoute><FormStatistics /></ProtectedRoute>} />
          <Route path="/forms/new" element={<ProtectedRoute><FormBuilder /></ProtectedRoute>} />
          <Route path="/forms/:id" element={<ProtectedRoute><FormBuilder /></ProtectedRoute>} />
          <Route path="/forms/:id/preview" element={<ProtectedRoute><FormPreviewPage /></ProtectedRoute>} />
          <Route path="/validations/new" element={<ProtectedRoute><ValidationBuilder /></ProtectedRoute>} />
          <Route path="/validations/:id" element={<ProtectedRoute><ValidationBuilder /></ProtectedRoute>} />
          <Route path="/clarity-configurations/new" element={<ProtectedRoute><ClarityConfigurationBuilder /></ProtectedRoute>} />
          <Route path="/clarity-configurations/:id" element={<ProtectedRoute><ClarityConfigurationBuilder /></ProtectedRoute>} />
          <Route path="/component-styles" element={<ProtectedRoute><ComponentStylesList /></ProtectedRoute>} />
          <Route path="/component-style-groups/:id" element={<ProtectedRoute><ComponentStyleGroupDetail /></ProtectedRoute>} />
          <Route path="/component-styles/new" element={<ProtectedRoute><ComponentStyleBuilder /></ProtectedRoute>} />
          <Route path="/component-styles/:id" element={<ProtectedRoute><ComponentStyleBuilder /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="*" element={<FormPreviewPage />} />
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <CssVarsProvider disableTransitionOnChange defaultMode="dark">
      <CssBaseline />
      <GlobalStyles
        styles={{
          ':root': {
            '--Collapsed-breakpoint': '769px',
            '--Cover-width': '40vw',
            '--Form-maxWidth': '700px',
            '--Transition-duration': '0.4s',
          },
          body: {
            backgroundColor: 'var(--color-abyss, #0e111b)',
            fontFamily: 'var(--font-inter), Inter, system-ui, sans-serif',
          },
        }}
      />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </CssVarsProvider>
  );
}

export default App;
